import styles from './styles.module.css';
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import DataTable from 'components/utils/DataTable';
import { Grid, Button, Chip, IconButton } from '@mui/material';
import { TRANSFER_STATUS, STATUS_COLOR_MAPPING, SUBSCRIPTION_TYPE } from 'const';
import { ShopContext } from 'Context/ShopContext';
import { InventoryContext } from 'Context/InventoryContext';
import { WarehouseContext } from 'Context/WarehouseContext';
import ShopsHelper from 'helpers/ShopsHelper';
import Loader from 'components/utils/Loading';
import StockTransferService from 'services/StockTransferService';
import { AlertMessagesContext } from 'react-alert-messages';

export default function StockTransferDetailsPopup(props) {
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const { t: tCommon } = useTranslation('common');
  const { shop } = useContext(ShopContext);
  const { getProducts, getStockBatches } = useContext(InventoryContext);
  const { warehouses, getWarehouses, getWarehouseProducts } = useContext(WarehouseContext);
  const { handleClose, transferData, handleSuccess } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(shop?.config?.default_warehouse || '');

  useEffect(() => {
    if (ShopsHelper.isWarehouseFeatureActive(shop)) {
      getWarehouses({ useCache: true });
    }
  }, [shop, getWarehouses]);

  useEffect(() => {
    if (ShopsHelper.isWarehouseFeatureActive(shop) && warehouses.length > 0 && !selectedWarehouseId) {
      const defaultWhId = shop?.config?.default_warehouse;
      const defaultWh = warehouses.find((w) => w.id === defaultWhId);
      setSelectedWarehouseId(defaultWh ? defaultWh.id : warehouses[0].id);
    }
  }, [warehouses, shop, selectedWarehouseId]);

  const listHeader = [
    { id: 'item.plu', label: tCommon('PLU'), type: 'text' },
    { id: 'item.name', label: tCommon('Item Name'), type: 'text' },
    { id: 'quantity', label: tCommon('Quantity'), type: 'text' },
    { id: 'rate', label: tCommon('Rate'), type: 'text' },
    {
      id: 'tax',
      label: tCommon('Tax'),
      type: 'callback',
      viewRender: (item) => `${item.tax + item.cess}` || '-',
    },
    { id: 'payable', label: tCommon('Payable'), type: 'text' },
  ];

  const _getInvoiceSummary = (items) => {
    let totalPrice = 0;
    let totalTax = 0;
    let totalAmount = 0;
    (items || []).forEach((item) => {
      totalPrice += item.rate;
      totalTax += item.tax + item.cess || 0;
      totalAmount += item.payable;
    });
    return {
      totalPrice: ShopsHelper.getAmountFormatted(shop, totalPrice),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
    };
  };

  const handleAcceptTransfer = async (warehouseId = null) => {
    setIsLoading(true);
    try {
    const payload = {};
      if (warehouseId) {
        payload.warehouse_id = warehouseId;
      }
      await StockTransferService.receiveStockTransfer(transferData.id);
      getProducts({ useCache: false });
       if (shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM) {
        getStockBatches({ useCache: false });
      }
      if (ShopsHelper.isWarehouseFeatureActive(shop)) {
        getWarehouseProducts({ useCache: false });
      }      postAlertMessage({ text: tCommon('Stock transfer accepted successfully'), type: 'success' });
      handleSuccess();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setIsLoading(false);
  };

  const handleRejectTransfer = async () => {
    setIsLoading(true);
    try {
      await StockTransferService.rejectStockTransfer(transferData.id);
      postAlertMessage({ text: tCommon('Stock transfer rejected successfully'), type: 'success' });
      handleSuccess();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setIsLoading(false);
  };

  const summary = _getInvoiceSummary(transferData?.data?.items);

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Stock Transfer Details')}</h2>
          <IconButton onClick={handleClose}><CloseIcon/></IconButton>
        </div>
        <div className={styles.detailsWrapper}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <Loader isOpen={true} backdropColor={'#ffffff'} />
            </div>
          ) : (
            <>
              {transferData?.status === 'TRANSIT' && (
                <div className={styles.customerDetails}>
                  <Grid container spacing={1} sx={{ ml: 1.25, mr: 1.25 }}>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        className={styles.draftBtn}
                        variant="outlined"
                        color="error"
                        onClick={() => handleRejectTransfer()}
                      >
                        {tCommon('Reject Transfer')}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        className={styles.draftBtn}
                        variant="outlined"
                        onClick={() => handleAcceptTransfer()}
                      >
                        {tCommon('Accept Transfer')}
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              )}
              <div className={styles.customerDetails}>
                <div>
                  <h3 className={styles.editTitle}>{tCommon('Shop ID')}</h3>
                  <h3 className={styles.customerData}>{transferData?.form_shop?.id}</h3>
                </div>
                <div>
                  <h3 className={styles.editTitle}>{tCommon('Shop Name')}</h3>
                  <h3 className={styles.customerData}>{transferData?.from_shop?.shop_name}</h3>
                </div>
                <div>
                  <h3 className={styles.editTitle}>{tCommon('Profit Percentage')}</h3>
                  <h3 className={styles.customerData}>{transferData?.profit_percentage}</h3>
                </div>
                <div>
                  <h3 className={styles.editTitle}>{tCommon('Status')}</h3>
                  <h3 className={styles.customerData}>
                    <Chip
                      className={styles.chip}
                      label={TRANSFER_STATUS[transferData?.status]}
                      color={STATUS_COLOR_MAPPING[transferData?.status] || 'default'}
                      size="small"
                    />
                  </h3>
                </div>
              </div>
              {transferData?.data?.remark && (
                <Grid container spacing={1} className={styles.subSection}>
                  <Grid item xs={3} className={styles.subSectionHeader}>
                    <h6 className={styles.textHead}>{tCommon('Remark')}</h6>
                  </Grid>
                  <Grid item xs={9}>
                    <h6 className={styles.textValues}>{transferData?.data?.remark}</h6>
                  </Grid>
                </Grid>
              )}
              <div className={styles.tableContent1}>
                <DataTable
                  columns={listHeader}
                  rows={transferData?.data?.items}
                  summary={{
                    'item.plu': 'Total',
                    rate: `${shop?.currency || ''} ${summary.totalPrice}`,
                    tax: `${shop?.currency || ''} ${summary.totalTax}`,
                    payable: `${shop?.currency || ''} ${summary.totalAmount}`,
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
